import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
  Button,
  Chip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { WEEKDAY_CODES, israelParts } from "../time";

const STATUS_COLOR = {
  scheduled: "info",
  recording: "warning",
  captured: "success",
  failed: "error",
};

function formatTime(utc) {
  const d = new Date(utc);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Dialog shown when an editor clicks a show in the guide. Lets them record just
 * this airing (oneshot) or set up a weekly repeat (recurring slot). If the show
 * is already scheduled, shows its status and a remove action.
 */
export default function RecordDialog({
  open,
  show,
  oneshot,
  recurring,
  busy,
  text,
  onClose,
  onRecordOnce,
  onRecordRecurring,
  onRemove,
}) {
  const [days, setDays] = useState([]);
  const [allowReruns, setAllowReruns] = useState(false);
  const [confirmReruns, setConfirmReruns] = useState(false);

  // Default the repeat selection to the show's own (Israel) weekday; reruns off.
  useEffect(() => {
    if (show) {
      setDays([israelParts(show.startTime).weekday]);
      setAllowReruns(false);
      setConfirmReruns(false);
    }
  }, [show]);

  if (!show) return null;

  const t = text.record;
  const dayLabels = text.days.abbr; // ["SUN", ... "SAT"], indexed Sun..Sat

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Stack spacing={0.5}>
          <Typography variant="h6" component="span" noWrap>
            {show.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatTime(show.startTime)} – {formatTime(show.endTime)}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* ── This airing (oneshot) ── */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t.thisAiring}
            </Typography>
            {oneshot ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  color={STATUS_COLOR[oneshot.status] || "default"}
                  label={t.status[oneshot.status] || oneshot.status}
                />
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={busy || oneshot.status === "recording"}
                  onClick={() => onRemove(oneshot.id)}
                >
                  {t.remove}
                </Button>
              </Stack>
            ) : (
              <Button
                size="small"
                variant="contained"
                disabled={busy}
                onClick={onRecordOnce}
              >
                {t.recordOnce}
              </Button>
            )}
          </Box>

          <Divider />

          {/* ── Repeat weekly (recurring) ── */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t.repeatWeekly}
            </Typography>
            {recurring ? (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip
                  size="small"
                  color="secondary"
                  label={`${t.repeatsOn} ${recurring.days
                    .map((d) => dayLabels[WEEKDAY_CODES.indexOf(d)])
                    .join(", ")}`}
                />
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  disabled={busy}
                  onClick={() => onRemove(recurring.id)}
                >
                  {t.stopRepeating}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <Typography variant="caption" color="text.secondary">
                  {t.pickDays}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  value={days}
                  onChange={(_e, next) => setDays(next)}
                  aria-label={t.repeatWeekly}
                >
                  {WEEKDAY_CODES.map((code, i) => (
                    <ToggleButton key={code} value={code} aria-label={code}>
                      {dayLabels[i]}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {/* Reruns (ש.ח) are excluded by default; ticking confirms intent. */}
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={allowReruns}
                      onChange={(e) =>
                        e.target.checked
                          ? setConfirmReruns(true)
                          : setAllowReruns(false)
                      }
                    />
                  }
                  label={
                    <Typography variant="caption">{t.includeReruns}</Typography>
                  }
                />
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  disabled={busy || days.length === 0}
                  onClick={() => onRecordRecurring(days, allowReruns)}
                  sx={{ alignSelf: "flex-start" }}
                >
                  {t.setupRepeat}
                </Button>
              </Stack>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t.close}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Confirm before opting a recurring in to recording reruns (ש.ח). */}
    <Dialog open={confirmReruns} onClose={() => setConfirmReruns(false)} maxWidth="xs" fullWidth>
      <DialogTitle>{t.rerunConfirm.title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2">{t.rerunConfirm.message}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmReruns(false)}>
          {t.rerunConfirm.cancel}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            setAllowReruns(true);
            setConfirmReruns(false);
          }}
        >
          {t.rerunConfirm.confirm}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
